/**
 * Parsing the physics hull out of the template SVG.
 *
 * The `chassis` object may be a `<polygon>`/`<polyline>` (a `points` attribute) or a `<path>` whose
 * `d` uses only straight segments - which is what Inkscape emits for a polygon drawn with the Bézier
 * tool without curve handles. Curves are deliberately unsupported: the hull is meant to be a crude
 * convex shape, and silently sampling a curve would let a sloppy trace become a sloppy collision
 * shape. `check:vehicles` reports a curve command as an error rather than guessing.
 *
 * Pure string maths, so it lives in core and is unit tested without a DOM.
 */

export interface Point {
  readonly x: number;
  readonly y: number;
}

export class PathParseError extends Error {}

/** Parse a `points` attribute: "96,258 904,206 904,452" (commas and/or whitespace). */
export function parsePointsAttr(attr: string): readonly Point[] {
  const numbers = attr
    .trim()
    .split(/[\s,]+/)
    .filter((token) => token.length > 0)
    .map(Number);

  if (numbers.some((n) => !Number.isFinite(n))) {
    throw new PathParseError(`points attribute has a non-numeric value: "${attr}"`);
  }
  if (numbers.length % 2 !== 0) {
    throw new PathParseError(`points attribute has an odd number of values: "${attr}"`);
  }

  const points: Point[] = [];
  for (let i = 0; i < numbers.length; i += 2) {
    points.push({ x: numbers[i] as number, y: numbers[i + 1] as number });
  }
  return points;
}

/**
 * Parse a straight-line path `d`. Supports M/m, L/l, H/h, V/v and Z/z. Anything else - notably any
 * curve command - is an error.
 */
export function parseStraightPathD(d: string): readonly Point[] {
  // Numbers first so an exponent's "e" is consumed as part of its number rather than read as a
  // command letter. Every letter is tokenised, including unsupported ones, so a curve command is
  // rejected below instead of being skipped and its coordinates silently treated as line vertices.
  const tokens = d.match(/-?\d*\.?\d+(?:e[-+]?\d+)?|[A-Za-z]/g) ?? [];
  const points: Point[] = [];

  let cursor: Point = { x: 0, y: 0 };
  let start: Point | null = null;
  let command: string | null = null;
  let index = 0;

  const nextNumber = (): number => {
    const token = tokens[index++];
    const value = Number(token);
    if (token === undefined || !Number.isFinite(value)) {
      throw new PathParseError(`expected a number in path data at token ${index}: "${d}"`);
    }
    return value;
  };

  while (index < tokens.length) {
    const token = tokens[index] as string;

    if (/^[A-Za-z]$/.test(token)) {
      if (!/^[MmLlHhVvZz]$/.test(token)) {
        throw new PathParseError(
          `unsupported path command "${token}" - the chassis hull must use straight segments only`,
        );
      }
      command = token;
      index++;
    } else if (command === null) {
      throw new PathParseError(`path data starts with a number, not a command: "${d}"`);
    } else if (command === 'M') {
      // A second coordinate pair after M is an implicit lineto, per the SVG spec.
      command = 'L';
      continue;
    } else if (command === 'm') {
      command = 'l';
      continue;
    }

    switch (command) {
      case 'M':
      case 'm': {
        const x = nextNumber();
        const y = nextNumber();
        cursor = command === 'M' ? { x, y } : { x: cursor.x + x, y: cursor.y + y };
        start = cursor;
        points.push(cursor);
        break;
      }
      case 'L':
      case 'l': {
        const x = nextNumber();
        const y = nextNumber();
        cursor = command === 'L' ? { x, y } : { x: cursor.x + x, y: cursor.y + y };
        points.push(cursor);
        break;
      }
      case 'H':
      case 'h': {
        const x = nextNumber();
        cursor = { x: command === 'H' ? x : cursor.x + x, y: cursor.y };
        points.push(cursor);
        break;
      }
      case 'V':
      case 'v': {
        const y = nextNumber();
        cursor = { x: cursor.x, y: command === 'V' ? y : cursor.y + y };
        points.push(cursor);
        break;
      }
      case 'Z':
      case 'z': {
        if (start !== null) cursor = start;
        command = null;
        break;
      }
      default:
        throw new PathParseError(
          `unsupported path command "${command}" - the chassis hull must use straight segments only`,
        );
    }
  }

  if (points.length === 0) throw new PathParseError(`path data produced no points: "${d}"`);
  return dropClosingDuplicate(points);
}

/** A polygon written as "M ... L ... L ... L <back to start> Z" would repeat its first point. */
function dropClosingDuplicate(points: readonly Point[]): readonly Point[] {
  const first = points[0];
  const last = points.at(-1);
  if (points.length > 2 && first !== undefined && last !== undefined) {
    if (Math.abs(first.x - last.x) < 1e-6 && Math.abs(first.y - last.y) < 1e-6) {
      return points.slice(0, -1);
    }
  }
  return points;
}

/** Signed area doubled. Positive means counter-clockwise in a y-down coordinate system. */
export function signedArea2(points: readonly Point[]): number {
  let total = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i] as Point;
    const b = points[(i + 1) % points.length] as Point;
    total += a.x * b.y - b.x * a.y;
  }
  return total;
}

/**
 * True if the polygon is convex (allowing collinear points). Box2D needs convex pieces, and the
 * validator would rather tell you at authoring time than let a concave hull produce a car that
 * catches on invisible geometry.
 */
export function isConvex(points: readonly Point[]): boolean {
  if (points.length < 3) return false;

  let sign = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i] as Point;
    const b = points[(i + 1) % points.length] as Point;
    const c = points[(i + 2) % points.length] as Point;
    const cross = (b.x - a.x) * (c.y - b.y) - (b.y - a.y) * (c.x - b.x);
    if (Math.abs(cross) < 1e-9) continue; // collinear: no information
    const current = cross > 0 ? 1 : -1;
    if (sign === 0) sign = current;
    else if (sign !== current) return false;
  }
  return true;
}

export function boundingBox(points: readonly Point[]): {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
} | null {
  if (points.length === 0) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const { x, y } of points) {
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }
  return { minX, minY, maxX, maxY };
}
